"use client";

import { useState, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { Badge, Button, Card, EmptyState } from "@/components/ui";
import { IconInbox } from "@/components/ui/icons";
import { formatSessionTime } from "@/lib/format";
import { markAllNotificationsReadAction, markNotificationReadAction } from "./actions";
import type { Locale } from "@/lib/mock";

export interface NotificationItem {
  id: string;
  type: "sos_triggered" | "safe_exit" | "message_flagged";
  createdAt: string;
  readAt: string | null;
  payload: Record<string, unknown>;
}

/**
 * Bandeja de notificaciones del admin (SOS / salida segura / mensaje
 * flaggeado) — visible directamente en el dashboard, no en un dropdown
 * separado. `notifications.service.listNotifications`/`markRead`/`markAllRead`.
 */
export function NotificationsPanel({ items }: { items: NotificationItem[] }) {
  const t = useTranslations("admin.dashboard");
  const tn = useTranslations("common.notifications");
  const locale = useLocale() as Locale;
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  const unread = items.filter((i) => !i.readAt);

  const handleMarkRead = (id: string) => {
    setError(null);
    startTransition(async () => {
      try {
        await markNotificationReadAction({ locale, notificationId: id });
        router.refresh();
      } catch {
        setError(t("actionError"));
      }
    });
  };

  const handleMarkAll = () => {
    setError(null);
    startTransition(async () => {
      try {
        await markAllNotificationsReadAction({ locale });
        router.refresh();
      } catch {
        setError(t("actionError"));
      }
    });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg text-ink">
          {t("notificationsTitle")}
          {unread.length > 0 && (
            <Badge variant="danger" dot className="ml-2 align-middle">
              {unread.length} {t("unread")}
            </Badge>
          )}
        </h2>
        {unread.length > 0 && (
          <Button size="sm" variant="ghost" onClick={handleMarkAll} disabled={isPending}>
            {tn("markAllRead")}
          </Button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-danger">{error}</p>}

      {items.length === 0 ? (
        <div className="mt-3">
          <EmptyState icon={<IconInbox size={20} />} title={t("notificationsEmpty")} />
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((item) => (
            <Card
              key={item.id}
              elevation={item.readAt ? "flat" : "raised"}
              className="flex flex-wrap items-center justify-between gap-2 p-3"
            >
              <div>
                <p className="text-sm font-medium text-ink">
                  {tn(`types.${item.type}`)}
                </p>
                <p className="nums mt-0.5 text-xs text-ink-muted">
                  {formatSessionTime(item.createdAt, locale)}
                </p>
              </div>
              {!item.readAt && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleMarkRead(item.id)}
                  disabled={isPending}
                >
                  {t("markRead")}
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
